import React from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { HomeChatHttpError } from './api';




export const REFUSAL_COPY =
  'FAR reference data is not available right now. Please try again later.';

export type ProcessingStopControlProps = {
  canStop: boolean;
  stopping: boolean;
  unavailableReason: string;
  onStop: () => void;
};

/**
 * Keep cancellation physically beside the work it describes. An operation is
 * cancellable only after Home Chat has received the owner-scoped operation id
 * for an ordinary turn. Until then (and for bound workflow work), preserve a
 * visible disabled affordance instead of guessing at a cancellation endpoint.
 */
export const ProcessingStopControl: React.FC<ProcessingStopControlProps> = ({
  canStop,
  stopping,
  unavailableReason,
  onStop,
}) => {
  const tooltip = stopping
    ? 'Stopping this response'
    : canStop
      ? 'Stop this response'
      : unavailableReason;
  return (
    <Tooltip title={tooltip} placement="top">
      <span style={{ display: 'inline-flex' }}>
        <Button
          data-testid="home-chat-processing-stop"
          size="small"
          color="warning"
          onClick={onStop}
          disabled={!canStop || stopping}
          aria-label={stopping ? 'Stopping…' : 'Stop'}
        >
          {stopping ? 'Stopping…' : 'Stop'}
        </Button>
      </span>
    </Tooltip>
  );
};

// Generic fallback shown when a server-side turn failure (HTTP 500) carries no
// usable friendly `detail.error` message.
export const GENERIC_TURN_ERROR_COPY = 'Something went wrong. Please try again.';

/**
 * Extract the backend's user-safe friendly message from a masked-crash 500.
 *
 * The v2 turn endpoint surfaces a real reader crash as
 * `HTTP 500 { detail: { error, error_code } }`. We render ONLY `detail.error`
 * (the boilerplate, user-safe message). `error_code` and any technical detail
 * are NEVER read here, matching the frontend error contract. Falls back to a
 * generic message if the body shape is anything else.
 */
export function friendlyTurnErrorMessage(e: unknown): string {
  if (e instanceof HomeChatHttpError) {
    const detail = (e.body as { detail?: unknown } | undefined)?.detail;
    if (detail && typeof detail === 'object' && 'error' in detail) {
      const msg = (detail as { error?: unknown }).error;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return GENERIC_TURN_ERROR_COPY;
}

/** True only for the server's actual per-session turn-admission conflict. */
export function isTurnInProgressError(e: unknown): boolean {
  return e instanceof HomeChatHttpError
    && e.status === 409
    && /(?:turn|response) (?:is |already )?in progress|in_progress/i.test(
      e.message
    );
}

/** The owner pressed Stop and the server has durably finalized that turn. */
export function isExpectedCancellationError(e: unknown): boolean {
  return e instanceof HomeChatHttpError
    && e.status === 409
    && /this request was cancelled/i.test(e.message);
}

/** Preserve the reviewed explanation for an unavailable workflow route. */
export function isUnavailableWorkflowError(e: unknown): boolean {
  return e instanceof HomeChatHttpError
    && e.status === 409
    && /choice is no longer available|workflow recommendation unavailable/i.test(
      e.message
    );
}

/** A chat exists, but its server-bound workflow was confirmed absent. */
export function isMissingBoundWorkflowError(e: unknown): boolean {
  if (!(e instanceof HomeChatHttpError) || e.status !== 404) return false;
  const detail = (e.body as { detail?: unknown } | undefined)?.detail;
  return Boolean(detail && typeof detail === 'object'
    && (detail as { error_code?: unknown }).error_code === 'workflow_no_longer_available');
}
